import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Lock, PlayCircle, ShieldCheck, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Panel } from "@/components/common/Panel";
import { useFmt } from "@/components/v52/Bits";
import { useT52 } from "@/i18n/v52";

export function errText(t52, tOr, e) {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object" && d.code) return tOr(`imp_err_${d.code}`, tOr(`imp_bl_${d.code}`, d.msg));
  return typeof d === "string" ? d : t52("imp_err_generic");
}

export function useImportCall(reload, setPending) {
  const { t52, tOr } = useT52();
  return useCallback(async (fn, okKey) => {
    setPending(true);
    try {
      await fn();
      toast.success(t52(okKey));
      return true;
    } catch (e) {
      toast.error(errText(t52, tOr, e));
      return false;
    } finally {
      setPending(false);
      reload();
    }
  }, [reload, setPending, t52, tOr]);
}

export async function downloadReport() {
  const r = await api.get("/control-import/report");
  const blob = new Blob([JSON.stringify(r.data, null, 1)], { type: "application/json" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `fundio-datenimport-bericht-${new Date().toISOString().slice(0, 16).replace(":", "")}.json`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export function JobChip({ job, testid }) {
  const { t52 } = useT52();
  if (!job) return <span className="f52-chip unk" data-testid={testid}>—</span>;
  const bad = job.status === "failed" || job.status === "interrupted" || (job.status === "done" && job.deviations?.length);
  const tone = bad ? "fail" : job.status === "done" ? "ok" : "unk";
  return <span className={`f52-chip ${tone}`} data-testid={testid}>{t52(`imp_js_${job.status}`)}</span>;
}

export function Counts({ obj, testid }) {
  const { tOr } = useT52();
  if (!obj) return null;
  return (
    <span className="f52-imp-counts" data-testid={testid}>
      {Object.entries(obj).map(([k, v]) => <span key={k}>{tOr(`imp_k_${k}`, k)} <b>{v ?? "—"}</b></span>)}
    </span>
  );
}

const lines = (obj) => Object.entries(obj || {}).map(([k, v]) => `${k}: ${v}`).join("\n") || "—";

function CensusFacts({ r }) {
  const { t52 } = useT52();
  const o = r.organizations;
  const l = r.locations;
  return (
    <dl className="f52-dl" data-testid="imp-census-facts">
      <div><dt>{t52("imp_c_orgs")}</dt><dd data-testid="imp-census-orgs">{o.total}</dd></div>
      <div><dt>{t52("imp_c_locs")}</dt><dd data-testid="imp-census-locs">{l.complete ? l.total : t52("imp_c_incomplete", { n: l.failed_count })}</dd></div>
      <div><dt>{t52("imp_c_qa")}</dt><dd>{o.qa}</dd></div>
      <div><dt>{t52("imp_c_nonqa")}</dt><dd>{o.non_qa}</dd></div>
      <div><dt>{t52("imp_c_status")}</dt><dd data-testid="imp-census-status">{lines(o.by_status_and_qa)}</dd></div>
      <div><dt>{t52("imp_c_source")}</dt><dd>{lines(o.by_source_system)}</dd></div>
      <div><dt>{t52("imp_c_placeholders")}</dt><dd data-testid="imp-census-placeholders">{r.local_qa_placeholders_not_counted?.join("\n") || "—"}</dd></div>
      <div><dt>{t52("imp_target_app")}</dt><dd>{t52(r.import_baseline_empty ? "imp_c_empty" : "imp_c_notempty")}</dd></div>
    </dl>
  );
}

export function CensusPanel({ census, fresh, busy, onRun }) {
  const { t52, tOr } = useT52();
  const fmt = useFmt();
  const r = census?.result;
  const ok = census?.status === "done" && !census.deviations?.length;
  const run = (
    <button type="button" className="f52-btn f52-btn-secondary" disabled={busy} onClick={onRun} data-testid="imp-census-run">
      <PlayCircle aria-hidden="true" />{t52("imp_c_run")}
    </button>
  );
  return (
    <Panel title={t52("imp_c_title")} subtitle={t52("imp_c_lead")} actions={run} testid="imp-census-panel">
      {!census ? <p className="f52-imp-muted">{t52("imp_c_none")}</p> : (
        <div className="f52-imp-body">
          <div className="f52-imp-line">
            <JobChip job={census} />
            {census.status === "done" && <span className={`f52-chip ${ok ? "ok" : "fail"}`} data-testid="imp-census-state">{t52(ok ? "imp_c_ok" : "imp_c_blocker")}</span>}
            <span>{t52("imp_checked", { time: fmt(census.finished_at || census.created_at) || "—" })}</span>
          </div>
          {ok && !fresh && <p className="f52-imp-warn"><AlertTriangle aria-hidden="true" />{t52("imp_c_stale")}</p>}
          {census.error && <p className="f52-imp-warn">{census.error}</p>}
          {r?.reason && <p className="f52-imp-warn" data-testid="imp-census-reason"><AlertTriangle aria-hidden="true" />{t52("imp_c_reason")}: {tOr(`imp_r_${r.reason}`, tOr(`rs_${r.reason}`, r.reason))}</p>}
          {r?.organizations && <CensusFacts r={r} />}
        </div>
      )}
    </Panel>
  );
}

export function SimPanel({ sim, canRun, busy, onRun }) {
  const { t52 } = useT52();
  const r = sim?.result;
  const checks = r?.checks || [];
  const fails = checks.filter((c) => !c.ok);
  const run = (
    <button type="button" className="f52-btn f52-btn-secondary" disabled={busy || !canRun} onClick={onRun} data-testid="imp-sim-run">
      <PlayCircle aria-hidden="true" />{t52("imp_s_run")}
    </button>
  );
  return (
    <Panel title={t52("imp_s_title")} subtitle={t52("imp_s_lead")} actions={run} testid="imp-sim-panel">
      {!sim ? <p className="f52-imp-muted">{t52("imp_s_none")}</p> : (
        <div className="f52-imp-body">
          <div className="f52-imp-line">
            <JobChip job={sim} testid="imp-sim-status" />
            {r && <strong data-testid="imp-sim-summary">{fails.length ? t52("imp_s_fail", { f: fails.length, n: checks.length }) : t52("imp_s_ok", { n: checks.length })}</strong>}
            {r?.final && <span>{t52("imp_s_final", { o: r.final.orgs_total, l: r.final.locations_total })}</span>}
          </div>
          {sim.error && <p className="f52-imp-warn">{sim.error}</p>}
          {fails.length > 0 && (
            <ul className="f52-imp-fails" data-testid="imp-sim-fails">
              {fails.map((c) => <li key={`${c.step}-${c.check}`}>{c.step} · {c.check}: {JSON.stringify(c.got)} ≠ {JSON.stringify(c.want)}</li>)}
            </ul>
          )}
        </div>
      )}
    </Panel>
  );
}

export function Field({ label, value, onChange, testid, area, numeric }) {
  const Tag = area ? "textarea" : "input";
  const extra = numeric ? { inputMode: "numeric", autoComplete: "one-time-code", pattern: "[0-9]{6}", maxLength: 6 } : {};
  return (
    <label className="f52-imp-field">{label}
      <Tag value={value} onChange={onChange} data-testid={testid} required {...extra} />
    </label>
  );
}

const EMPTY = { snapshot_id: "", created_at: "", restore_path: "" };

export function BackupPanel({ backup, busy, onSave }) {
  const { t52 } = useT52();
  const fmt = useFmt();
  const [f, setF] = useState({ control_db: EMPTY, app_db: EMPTY });
  const set = (db, k) => (e) => setF((s) => ({ ...s, [db]: { ...s[db], [k]: e.target.value } }));
  const submit = async (e) => { e.preventDefault(); if (await onSave(f)) setF({ control_db: EMPTY, app_db: EMPTY }); };
  return (
    <Panel title={t52("imp_b_title")} subtitle={t52("imp_b_lead")} testid="imp-backup-panel">
      <div className="f52-imp-body">
        <p className={backup ? "f52-imp-line" : "f52-imp-warn"} data-testid="imp-backup-state">
          {backup ? t52("imp_b_saved", { by: backup.recorded_by, time: fmt(backup.recorded_at) })
            : <><AlertTriangle aria-hidden="true" />{t52("imp_b_missing")}</>}
        </p>
        {backup && <p className="f52-mono" data-testid="imp-backup-ids">CONTROL {backup.control_db?.snapshot_id} · APP {backup.app_db?.snapshot_id}</p>}
      </div>
      <form className="f52-imp-form" onSubmit={submit} data-testid="imp-backup-form">
        {["control_db", "app_db"].map((db) => (
          <fieldset key={db}>
            <legend>{t52(db === "control_db" ? "imp_b_control" : "imp_b_app")}</legend>
            <Field label={t52("imp_b_sid")} value={f[db].snapshot_id} onChange={set(db, "snapshot_id")} testid={`imp-backup-${db}-sid`} />
            <Field label={t52("imp_b_time")} value={f[db].created_at} onChange={set(db, "created_at")} testid={`imp-backup-${db}-time`} />
            <Field area label={t52("imp_b_restore")} value={f[db].restore_path} onChange={set(db, "restore_path")} testid={`imp-backup-${db}-restore`} />
          </fieldset>
        ))}
        <button type="submit" className="f52-btn f52-btn-secondary" disabled={busy} data-testid="imp-backup-save">
          <ShieldCheck aria-hidden="true" />{t52("imp_b_save")}
        </button>
      </form>
    </Panel>
  );
}

function JobLine({ label, job, testid }) {
  const { t52 } = useT52();
  const note = job?.deviations?.length ? `${t52("imp_dev")}: ${job.deviations.join(" · ")}`
    : job?.status === "done" ? t52("imp_nodev") : job?.error;
  return (
    <p>{label}: <JobChip job={job} testid={testid} /><Counts obj={job?.result?.counts} />{note && <span>{note}</span>}</p>
  );
}

function StepRow({ s, busy, canDry, preview, onDry, onExec }) {
  const { t52 } = useT52();
  const blocked = s.blockers?.length > 0;
  const lockTest = preview && !busy;
  return (
    <li data-testid={`imp-step-${s.key}`}>
      <div>
        <h3>{s.order} · {t52(`imp_step_${s.key}`)}</h3>
        <p>{t52("imp_expected")}: <Counts obj={s.expected} /></p>
        <p>{t52("imp_target_short")}: <span className="f52-mono">{s.target}</span></p>
        <JobLine label={t52("imp_last_dry")} job={s.last_dry_run} testid={`imp-step-${s.key}-dry-status`} />
        <JobLine label={t52("imp_last_exec")} job={s.last_execute} testid={`imp-step-${s.key}-exec-status`} />
        {blocked && (
          <p className="f52-imp-warn" data-testid={`imp-step-${s.key}-blockers`}>
            <Lock aria-hidden="true" />{t52("imp_blocked")}: {s.blockers.map((c) => t52(`imp_bl_${c}`)).join(" · ")}
          </p>
        )}
      </div>
      <div className="acts">
        <button type="button" className="f52-btn f52-btn-secondary" disabled={busy || !canDry} onClick={() => onDry(s.key)} data-testid={`imp-step-${s.key}-dry`}>
          <PlayCircle aria-hidden="true" />{t52("imp_dry")}
        </button>
        <button type="button" className="f52-btn f52-btn-danger" disabled={!lockTest && (busy || blocked)} onClick={() => onExec(s)} data-testid={`imp-step-${s.key}-exec`}>
          <ShieldCheck aria-hidden="true" />{t52("imp_exec")}
        </button>
      </div>
    </li>
  );
}

export function StepsPanel({ steps, busy, canDry, preview, onDry, onExec }) {
  const { t52 } = useT52();
  return (
    <Panel title={t52("imp_st_title")} testid="imp-steps-panel" noPad>
      <ol className="f52-imp-steps">
        {steps.map((s) => <StepRow key={s.key} s={s} busy={busy} canDry={canDry} preview={preview} onDry={onDry} onExec={onExec} />)}
      </ol>
    </Panel>
  );
}

// Bestätigungsdialog: Wort + Ziel + frischer 2FA-Code. Esc bricht ab, Fokus bleibt im Dialog.
export function ExecuteDialog({ step, preview, onCancel, onSubmit }) {
  const { t52 } = useT52();
  const ref = useRef(null);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  const [v, setV] = useState({ confirm: "", target_confirm: "", totp_code: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const prev = document.activeElement;
    ref.current?.querySelector("input")?.focus();
    const onKey = (e) => { if (e.key === "Escape") { e.preventDefault(); cancelRef.current(); } };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); prev?.focus?.(); };
  }, []);
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => { e.preventDefault(); setBusy(true); await onSubmit(v); setBusy(false); };
  const name = t52(`imp_step_${step.key}`);
  return (
    <div ref={ref} className="f52-dlg" role="alertdialog" aria-modal="true" aria-labelledby="imp-dlg-t" data-testid="imp-exec-dialog"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <form className="box" onSubmit={submit}>
        <h2 id="imp-dlg-t">{t52("imp_d_title", { step: name })}</h2>
        <p>{t52("imp_d_lead")}</p>
        {preview && <p className="f52-imp-warn" data-testid="imp-exec-preview-note"><Lock aria-hidden="true" />{t52("imp_d_preview")}</p>}
        {step.blockers?.length > 0 && (
          <p className="f52-imp-warn" data-testid="imp-exec-blockers">{t52("imp_blocked")}: {step.blockers.map((c) => t52(`imp_bl_${c}`)).join(" · ")}</p>
        )}
        <Field label={t52("imp_d_word", { w: step.confirm_word })} value={v.confirm} onChange={set("confirm")} testid="imp-exec-confirm" />
        <Field label={t52("imp_d_target", { t: step.target })} value={v.target_confirm} onChange={set("target_confirm")} testid="imp-exec-target" />
        <Field label={t52("imp_d_totp")} value={v.totp_code} onChange={set("totp_code")} testid="imp-exec-totp" numeric />
        <div className="acts">
          <button type="button" className="f52-btn f52-btn-secondary" onClick={onCancel} data-testid="imp-exec-cancel">{t52("imp_d_cancel")}</button>
          <button type="submit" className="f52-btn f52-btn-danger-solid" disabled={busy} data-testid="imp-exec-submit">
            {busy ? <Loader2 aria-hidden="true" className="f52-spin" /> : <ShieldCheck aria-hidden="true" />}{t52("imp_d_go")}
          </button>
        </div>
      </form>
    </div>
  );
}
